import {
  getMultiMarketDataSnapshotRequestKey,
  type SecurityMarketDataCellValue,
  type UseMultiMarketDataSnapshotRequest,
} from '@talos/kyoko';
import {
  getMarketDataSnapshotForRequestKey,
  getMarketDataSnapshotSizeBucketLevel,
  toSecurityMarketDataCellValue,
} from '@talos/kyoko/src/components/BlotterTable/columns/securityMarketData';
import { getPriceGridRequestMarkets, PRICE_GRID_MARKET_DATA_COLLECTION_PATH } from '../../columns';
import type { PriceGridEntity } from '../../PriceGridEntity';
import { PredictionMarketOutcomeRow } from './rows';

/** Row field holding the size bucket a row shows; the grid renders one bucket per row. */
const PREDICTION_MARKET_SIZE_BUCKET_FIELD = 'bucket.size';

export interface PredictionMarketDataContext {
  row: PriceGridEntity | undefined;
  /** The market account selected in the header; scopes every request this grid makes. */
  marketAccountName: string | undefined;
}

function getPredictionMarketSizeBuckets(row: PredictionMarketOutcomeRow): string[] {
  const sizeBuckets = row.security.SizeBuckets?.filter(
    (sizeBucket): sizeBucket is string => typeof sizeBucket === 'string' && sizeBucket.length > 0
  );
  if (sizeBuckets && sizeBuckets.length > 0) {
    return sizeBuckets;
  }

  return row.security.NormalSize ? [row.security.NormalSize] : [];
}

/**
 * The only place prediction market rows turn into market data requests. Both the subscription side and the
 * cell values go through here, so a cell can never read a snapshot under a key nothing subscribed to.
 */
export function getPredictionMarketDataRequest({
  row,
  marketAccountName,
}: PredictionMarketDataContext): UseMultiMarketDataSnapshotRequest | undefined {
  if (!(row instanceof PredictionMarketOutcomeRow)) {
    return undefined;
  }

  const symbol = row.security.Symbol;
  const sizeBuckets = getPredictionMarketSizeBuckets(row);
  if (!symbol || sizeBuckets.length === 0) {
    return undefined;
  }

  const markets = getPriceGridRequestMarkets({
    selectedMarkets: marketAccountName ? [marketAccountName] : [],
    securityMarkets: row.security.Markets,
  });
  // An outcome that does not trade on the selected account must not fall back to an unscoped request.
  if (marketAccountName && !markets) {
    return undefined;
  }

  return {
    key: getMultiMarketDataSnapshotRequestKey({ Symbol: symbol, Markets: markets, SizeBuckets: sizeBuckets }),
    Symbol: symbol,
    Markets: markets,
    SizeBuckets: sizeBuckets,
  };
}

export function getPredictionMarketDataCellValue({
  row,
  marketAccountName,
}: PredictionMarketDataContext): SecurityMarketDataCellValue {
  const request = getPredictionMarketDataRequest({ row, marketAccountName });
  if (!request) {
    return undefined;
  }

  const snapshot = getMarketDataSnapshotForRequestKey(row, PRICE_GRID_MARKET_DATA_COLLECTION_PATH, request.key);
  const sizeBucketLevel = getMarketDataSnapshotSizeBucketLevel(row, snapshot, PREDICTION_MARKET_SIZE_BUCKET_FIELD);
  if (!sizeBucketLevel) {
    return undefined;
  }

  return toSecurityMarketDataCellValue(snapshot, sizeBucketLevel);
}
